import type { ReactNode } from 'react';
import { clip, fraction, niceMax, textWidth } from './scale';
import './charts.css';

export interface Bar {
  label: string;
  value: number;
  /** The previous period's value, drawn as a ghost behind the bar. */
  previous?: number;
  /** Where the bar leads (every number opens its companies). */
  href?: string;
  /** Shown at the end of the bar instead of the raw value. */
  valueText?: string;
}

export interface BarsProps {
  bars: readonly Bar[];
  label: string;
  /** Renders a link for a bar with an href; the router's Link in the app, a plain <a> in the catalogue. */
  renderLink?: (href: string, children: ReactNode) => ReactNode;
  format?: (n: number) => string;
  width?: number;
}

const ROW = 28;
const LABEL_W = 132;
/** What fits in the label column at 11px before it would run off the left edge. */
const LABEL_CHARS = 20;
const VALUE_W = 64;

function BarRow({
  bar,
  i,
  top,
  width,
  valueW,
  format,
}: {
  bar: Bar;
  i: number;
  top: number;
  width: number;
  valueW: number;
  format: (n: number) => string;
}): ReactNode {
  const y = i * ROW;
  const innerW = width - LABEL_W - valueW;
  const w = innerW * fraction(bar.value, top);
  const ghostW = bar.previous === undefined ? 0 : innerW * fraction(bar.previous, top);
  const clipped = clip(bar.label, LABEL_CHARS);
  return (
    <g transform={`translate(0 ${y})`}>
      <text className="pui-chart__label" x={LABEL_W - 10} y={ROW / 2 + 4} textAnchor="end">
        {clipped !== bar.label ? <title>{bar.label}</title> : null}
        {clipped}
      </text>
      <BarShapes w={w} ghostW={ghostW} empty={bar.value === 0} />
      <text className="pui-chart__value" x={LABEL_W + Math.max(w, ghostW) + 8} y={ROW / 2 + 4}>
        {bar.valueText ?? format(bar.value)}
      </text>
    </g>
  );
}

/** The ghost of the previous period behind the bar itself; an empty bar is a hairline, not nothing. */
function BarShapes({ w, ghostW, empty }: { w: number; ghostW: number; empty: boolean }): ReactNode {
  return (
    <>
      {ghostW > 0 ? (
        <rect
          className="pui-chart__ghost"
          x={LABEL_W}
          y={ROW / 2 - 9}
          width={ghostW}
          height={18}
          rx={3}
        />
      ) : null}
      <rect
        className="pui-chart__bar"
        x={LABEL_W}
        y={ROW / 2 - 6}
        width={Math.max(w, empty ? 0 : 2)}
        height={12}
        rx={3}
      />
    </>
  );
}

/** Room for the longest value text, so "12 · mostly 409 NameTaken" never runs past the edge. */
function valueColumn(bars: readonly Bar[], format: (n: number) => string): number {
  const longest = Math.max(0, ...bars.map((b) => textWidth(b.valueText ?? format(b.value))));
  return Math.max(VALUE_W, Math.ceil(longest) + 12);
}

/**
 * Horizontal bars, one per row, sorted as given; the previous period ghosted behind each.
 * Nothing at all for no bars: the screen says why in words. With links inside, the SVG is a
 * group so a screen reader reaches each one; without, it is one picture with a spoken label.
 */
export function Bars({
  bars,
  label,
  renderLink,
  format = String,
  width = 480,
}: BarsProps): ReactNode {
  if (bars.length === 0) return null;
  const top = niceMax(
    bars.flatMap((b) => (b.previous === undefined ? [b.value] : [b.value, b.previous])),
  );
  const valueW = valueColumn(bars, format);
  const linked = renderLink !== undefined && bars.some((b) => b.href);
  return (
    <svg
      className="pui-chart"
      viewBox={`0 0 ${width} ${bars.length * ROW}`}
      role={linked ? 'group' : 'img'}
      aria-label={label}
    >
      {bars.map((bar, i) => {
        const row = (
          <BarRow
            key={bar.label}
            bar={bar}
            i={i}
            top={top}
            width={width}
            valueW={valueW}
            format={format}
          />
        );
        return bar.href && renderLink ? <g key={bar.label}>{renderLink(bar.href, row)}</g> : row;
      })}
    </svg>
  );
}
